import { Injectable, inject } from "@angular/core";
import { Observable, map, catchError, of, switchMap } from "rxjs";
import { UserDefinedTemplateVariable } from "../models/dynamic-variable.model";
import { CmsService } from "libs/cms/src/lib/services/cms.lib.service";
import { CmsBlockKey, CmsBlockModel } from "libs/cms/src/lib/models/cms.model";

/**
 * Provides template variable definitions by merging built-in constants
 * with user-defined variables stored in a CMS block.
 */
@Injectable({ providedIn: "root" })
export class MessageTemplateVariableService {
	private readonly cms = inject(CmsService);
	private readonly cmsKey = CmsBlockKey.TMA_MESSAGE_TEMPLATE_VARIABLES;

	getUserDefinedVariables(): Observable<UserDefinedTemplateVariable[]> {
		return this.cms.getCmsBlock<UserDefinedTemplateVariable[]>(this.cmsKey).pipe(
			map(block => block.content),
			catchError(() => of([])),
		);
	}

	getBlock(): Observable<CmsBlockModel<UserDefinedTemplateVariable[]>> {
		return this.cms.getCmsBlock<UserDefinedTemplateVariable[]>(this.cmsKey);
	}

	updateVariables(
		variables: UserDefinedTemplateVariable[]
	): Observable<UserDefinedTemplateVariable[]> {
		return this.cms.updateCmsBlock(this.cmsKey, { content: variables }).pipe(
			map(block => block.content),
		);
	}

	addVariable(variable: UserDefinedTemplateVariable): Observable<unknown> {
		return this.getUserDefinedVariables().pipe(
			switchMap(existing => this.updateVariables([...existing, variable])),
		);
	}

	removeVariable(name: string): Observable<unknown> {
		return this.getUserDefinedVariables().pipe(
			switchMap(existing => this.updateVariables(existing.filter(v => v.name !== name))),
		);
	}
}
